import React, { useCallback, useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { Capacitor } from '@capacitor/core';
import { db, auth } from '../firebase/config';
import { A2HS, a2hsState, snooze, dismissForever, IOS_STEPS } from '../utils/addToHomeScreen';

// ── "PUT ROOFBEAM ON YOUR HOME SCREEN" ──────────────────────────────────────
//
// Shown once, after login, on a phone in an ordinary browser tab.
//
// ⚠ WHY IT EARNS THE INTERRUPTION. On iOS, web push works ONLY from the Home
// Screen app — in Safari, notification permission cannot be granted at all.
// So for an iPhone crew member this is not "nicer, like an app": it is the
// only route to ever being told about a job or a schedule change. The card
// leads with that, not with the icon.
//
// ⚠ AND iOS CANNOT BE PROMPTED. There is no beforeinstallprompt on iOS and no
// API that installs a web app; Apple requires the Share sheet, by hand. So
// iPhone gets numbered steps and Android gets a real button — one "Install"
// button would simply do nothing on half the phones.
//
// ⚠ IT NEVER APPEARS INSIDE THE APP. Native, or an already-installed Home
// Screen launch, both return HIDE. A card telling somebody to install the
// thing they are holding is the worst outcome available here.
export default function AddToHomeScreenPrompt() {
  const [state, setState] = useState(A2HS.HIDE);
  const [signedIn, setSignedIn] = useState(false);
  const [apkUrl, setApkUrl] = useState('');
  const [installEvent, setInstallEvent] = useState(null);
  const [busy, setBusy] = useState(false);

  const native = (() => { try { return Capacitor.isNativePlatform(); } catch (_) { return false; } })();

  // ⚠ AFTER LOGIN, NOT ON LANDING. An anonymous visitor on a public signing or
  // report link has no reason to install a CRM, and this component is mounted
  // app-wide — including on those pages.
  useEffect(() => auth.onAuthStateChanged(u => setSignedIn(!!u)), []);

  const refresh = useCallback(() => setState(a2hsState({ native })), [native]);
  useEffect(() => { if (signedIn) refresh(); else setState(A2HS.HIDE); }, [signedIn, refresh]);

  // ⚠ THE ANDROID INSTALL PROMPT ARRIVES ON ITS OWN SCHEDULE and can only be
  // fired from a user gesture later, so it is captured whenever Chrome offers
  // it. preventDefault stops Chrome's own mini-infobar so there is one ask,
  // not two.
  useEffect(() => {
    const onPrompt = e => { e.preventDefault(); setInstallEvent(e); };
    const onInstalled = () => { dismissForever(); setState(A2HS.HIDE); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  // The real APK, the same record the in-app updater reads, so the card can
  // never offer a link to a build that does not exist.
  useEffect(() => {
    if (state !== A2HS.ANDROID) return;
    getDoc(doc(db, 'settings', 'company'))
      .then(s => setApkUrl(String((s.exists() && s.data().latestAndroid && s.data().latestAndroid.apkUrl) || '')))
      .catch(() => setApkUrl(''));
  }, [state]);

  if (!signedIn || state === A2HS.HIDE) return null;

  const close = (forever) => {
    setBusy(true);
    if (forever) dismissForever(); else snooze(7);
    setState(A2HS.HIDE);
  };

  const wrap = {
    position: 'fixed', left: 12, right: 12, bottom: 12, zIndex: 9000,
    background: '#1a1d2e', border: '1px solid rgba(255,255,255,0.14)',
    borderRadius: 14, padding: '16px 16px 14px', color: '#fff',
    boxShadow: '0 12px 40px rgba(0,0,0,0.5)', maxWidth: 520, margin: '0 auto',
    fontFamily: 'inherit',
  };
  const h = { fontSize: 16, fontWeight: 700, lineHeight: 1.3, marginBottom: 6 };
  const p = { fontSize: 13.5, color: 'rgba(255,255,255,0.68)', lineHeight: 1.55 };
  const primary = {
    background: '#e91e8c', border: 'none', borderRadius: 9, color: '#fff',
    padding: '13px 18px', fontSize: 15, fontWeight: 700, cursor: 'pointer',
    width: '100%', minHeight: 50, marginTop: 12, fontFamily: 'inherit',
  };
  const ghost = {
    background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.55)',
    padding: '10px 6px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
    fontFamily: 'inherit',
  };
  const row = { display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between', marginTop: 10 };

  async function androidInstall() {
    if (!installEvent) return;
    setBusy(true);
    try {
      installEvent.prompt();
      const choice = await installEvent.userChoice;
      // Accepting fires `appinstalled` too; dismissing should not nag again
      // straight away, so it snoozes rather than reappearing on next login.
      if (choice && choice.outcome === 'accepted') dismissForever(); else snooze(7);
      setState(A2HS.HIDE);
    } catch (_) {
      setBusy(false);
    }
  }

  const body = () => {
    if (state === A2HS.IOS_OTHER) {
      return (
        <>
          <div style={h}>🧭 Open this page in Safari</div>
          <div style={p}>
            Only Safari can add RoofBeam to your iPhone's Home Screen — Chrome and the
            others have no "Add to Home Screen" at all. Copy this page's address, open
            Safari, paste it in, and the steps will be waiting for you.
          </div>
          <div style={{ ...p, marginTop: 8, color: 'rgba(255,255,255,0.5)' }}>
            It matters because notifications only work from the Home Screen icon.
          </div>
        </>
      );
    }
    if (state === A2HS.ANDROID) {
      return (
        <>
          <div style={h}>📱 Put RoofBeam on your phone</div>
          <div style={p}>
            {installEvent
              ? 'Install it and it opens like any other app — full screen, with notifications.'
              : 'Install the RoofBeam app and it opens full screen, with notifications.'}
          </div>
          {installEvent ? (
            <button type="button" style={{ ...primary, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={androidInstall}>
              Install RoofBeam
            </button>
          ) : apkUrl ? (
            <a href={apkUrl} style={{ ...primary, display: 'block', textAlign: 'center', textDecoration: 'none', boxSizing: 'border-box' }}>
              Download the RoofBeam app
            </a>
          ) : (
            <div style={{ ...p, marginTop: 10 }}>
              Open your browser menu (⋮) and tap <strong>Install app</strong> or{' '}
              <strong>Add to Home screen</strong>.
            </div>
          )}
        </>
      );
    }
    // IOS_SAFARI
    return (
      <>
        <div style={h}>🏠 Add RoofBeam to your Home Screen</div>
        {/* ⚠ THE REASON FIRST. "It looks nicer" gets dismissed; "this is the
            only way your phone can notify you" does not. */}
        <div style={p}>
          iPhone can't install apps on its own, so it takes four taps. It's also
          the only way notifications can reach you — Safari can't do them.
        </div>
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 9 }}>
          {IOS_STEPS.map((s, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <span style={{ fontSize: 17, lineHeight: 1.35, width: 24, flexShrink: 0, textAlign: 'center' }}>{s.icon}</span>
              <span style={{ fontSize: 13.5, lineHeight: 1.45, color: 'rgba(255,255,255,0.88)' }}>
                <strong style={{ color: 'rgba(255,255,255,0.45)', marginRight: 6 }}>{i + 1}.</strong>{s.text}
              </span>
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <div style={wrap} role="dialog" aria-label="Add RoofBeam to your Home Screen">
      {body()}
      <div style={row}>
        {/* ⚠ TWO DIFFERENT EXITS, and the labels have to say which is which.
            "Not now" comes back in a week; "Done" never comes back on this
            device. A single × would make people guess. */}
        <button type="button" style={ghost} disabled={busy} onClick={() => close(false)}>Not now</button>
        <button type="button" style={{ ...ghost, color: 'rgba(255,255,255,0.85)' }} disabled={busy} onClick={() => close(true)}>
          Done — don't show again
        </button>
      </div>
    </div>
  );
}
